// Segmented — a row of mutually exclusive options.
//
// Used both as page tabs (Active / History / Arsip) and as view switches
// (Kartu / Daftar). This was copy-pasted as a local `TabButton` in five files;
// one component means the active style, the role attributes and the focus ring
// cannot drift apart.

import type { ReactNode } from 'react';
import { cn } from '@/lib/format';

export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
  icon?: ReactNode;
  title?: string;
}

export interface SegmentedProps<T extends string> {
  value: T;
  onChange: (next: T) => void;
  options: SegmentedOption<T>[];
  /** `tabs` announces as a tablist (page sections); `radio` as a radiogroup (a setting). */
  role?: 'tabs' | 'radio';
  'aria-label'?: string;
  size?: 'sm' | 'md';
  className?: string;
}

export function Segmented<T extends string>({
  value,
  onChange,
  options,
  role = 'tabs',
  'aria-label': ariaLabel,
  size = 'md',
  className,
}: SegmentedProps<T>) {
  const groupRole = role === 'tabs' ? 'tablist' : 'radiogroup';
  const itemRole = role === 'tabs' ? 'tab' : 'radio';
  return (
    <div
      role={groupRole}
      aria-label={ariaLabel}
      className={cn('inline-flex p-1 rounded bg-canvas border border-line', className)}
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role={itemRole}
            {...(role === 'tabs' ? { 'aria-selected': active } : { 'aria-checked': active })}
            title={o.title}
            onClick={() => onChange(o.value)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded font-medium transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
              size === 'sm' ? 'px-2.5 py-1 text-caption' : 'px-3 py-1.5 text-small',
              active ? 'bg-surface text-ink shadow-hover' : 'text-ink-subtle hover:text-ink',
            )}
          >
            {o.icon}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
